import styled from "styled-components";
import { ChevronLeft } from "lucide-react";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

const ColumnContainer = styled.div<{ $width?: string; $flex?: boolean }>`
  width: ${({ $width, $flex }) => ($flex ? "auto" : $width || "300px")};
  min-width: ${({ $width, $flex }) => ($flex ? "0" : $width || "300px")};
  ${({ $flex }) => ($flex ? "flex: 1;" : "")}
  border-right: 1px solid ${colors.border};
  background-color: ${colors.background};
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  transition: transform 0.3s ease-in-out;

  @media (max-width: 768px) {
    width: 100%;
    min-width: 100%;
    border-right: none;
    flex: ${({ $flex }) => ($flex ? 1 : "none")};
    min-height: 0;
  }
`;

const ColumnHeader = styled.div<{ $dense?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  padding: ${({ $dense }) => ($dense ? spacing.x2 : spacing.x3)}px;
  border-bottom: 1px solid ${colors.border};
  background-color: ${colors.backgroundLight};
  font-weight: 600;
  color: ${colors.text};
  font-size: ${({ $dense }) => ($dense ? fontSizes.small : fontSizes.regular)}px;
`;

const BackButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: none;
  color: ${colors.text};
  cursor: pointer;

  &:hover {
    background-color: ${colors.backgroundDark};
  }

  @media (max-width: 768px) {
    display: flex;
  }
`;

const HeaderTitle = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeaderSubtitle = styled.div`
  font-size: ${fontSizes.small}px;
  font-weight: 400;
  color: ${colors.textMuted};
  margin-top: ${spacing.x05}px;
`;

const ColumnContent = styled.div<{ $dense?: boolean }>`
  flex: 1;
  padding: ${({ $dense }) => ($dense ? spacing.x1 : spacing.x2)}px;
  display: flex;
  flex-direction: column;
  gap: ${({ $dense }) => ($dense ? spacing.x1 : spacing.x2)}px;
`;

type ListColumnProps = {
  title?: string;
  subtitle?: string;
  width?: string;
  flex?: boolean;
  dense?: boolean;
  onBack?: () => void;
  children: React.ReactNode;
};

export const ListColumn = ({
  title,
  subtitle,
  width,
  flex = false,
  dense = false,
  onBack,
  children,
}: ListColumnProps) => {
  return (
    <ColumnContainer $width={width} $flex={flex}>
      {title && (
        <ColumnHeader $dense={dense}>
          {onBack && (
            <BackButton type="button" onClick={onBack} aria-label="Back">
              <ChevronLeft size={20} />
            </BackButton>
          )}
          <HeaderTitle>
            {title}
            {subtitle && <HeaderSubtitle>{subtitle}</HeaderSubtitle>}
          </HeaderTitle>
        </ColumnHeader>
      )}
      <ColumnContent $dense={dense}>{children}</ColumnContent>
    </ColumnContainer>
  );
};
